import { useState, useCallback, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Trash2 } from 'lucide-react'
import { OperatorSelect } from './operator-select'
import { TEXT_OPERATORS } from './filter-types'
import type { FilterOperator } from '@/types/api'

interface TextFilterEditorProps {
  value: string
  operator: FilterOperator
  onValueChange: (value: string) => void
  onOperatorChange: (operator: FilterOperator) => void
  onRemove?: () => void
  label?: string
  placeholder?: string
}

export function TextFilterEditor({
  value,
  operator,
  onValueChange,
  onOperatorChange,
  onRemove,
  label,
  placeholder = 'Enter value...',
}: TextFilterEditorProps) {
  const [localValue, setLocalValue] = useState(value)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleApply = useCallback(() => {
    onValueChange(localValue)
  }, [localValue, onValueChange])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleApply()
      }
    },
    [handleApply]
  )

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex items-center gap-2">
        <OperatorSelect
          value={operator}
          options={TEXT_OPERATORS}
          onChange={onOperatorChange}
          label={label}
          className="flex-1 h-8"
        />
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="h-8 w-8 shrink-0"
          >
            <Trash2 className="h-4 w-4 text-destructive/50" />
          </Button>
        )}
      </div>
      <Input
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="h-8 text-sm"
        autoFocus
      />
      <Button size="sm" onClick={handleApply} className="w-full">
        Apply
      </Button>
    </div>
  )
}
